import {createThemes, type Palette} from '@bsky.app/alf'

const PINK = '#EC4899'
const YELLOW = '#FFC404'
const SAKURA_LIKE = '#FF85C2'

// pink and like values should always be matching due to
// deprecation of the like value by BlueSky in palettes
// node_modules/@bsky.app/alf/src/palette.ts
// Unless otherwise needed 🐙

// ---------------------------------------------------------------------------
// NYXO SKY
// Deep indigo-navy contrast ramp, violet-blue accent.
// ---------------------------------------------------------------------------
const NYXOSKY_DEFAULT_PALETTE: Palette = {
  white: 'hsl(245, 20%, 100%)',
  black: 'hsl(245, 38%, 6%)',
  pink: PINK,
  yellow: YELLOW,
  like: PINK,

  contrast_0: 'hsl(245, 20%, 100%)',
  contrast_25: 'hsl(245, 18%, 96%)',
  contrast_50: 'hsl(245, 16%, 92%)',
  contrast_100: 'hsl(245, 15%, 86%)',
  contrast_200: 'hsl(245, 14%, 78%)',
  contrast_300: 'hsl(245, 13%, 67%)',
  contrast_400: 'hsl(245, 13%, 56%)',
  contrast_500: 'hsl(245, 14%, 46%)',
  contrast_600: 'hsl(245, 16%, 37%)',
  contrast_700: 'hsl(245, 18%, 28%)',
  contrast_800: 'hsl(245, 22%, 20%)',
  contrast_900: 'hsl(245, 26%, 15%)',
  contrast_950: 'hsl(245, 30%, 11%)',
  contrast_975: 'hsl(245, 34%, 8%)',
  contrast_1000: 'hsl(245, 38%, 6%)',

  primary_25: 'hsl(255, 90%, 98%)',
  primary_50: 'hsl(255, 88%, 95%)',
  primary_100: 'hsl(255, 85%, 90%)',
  primary_200: 'hsl(255, 82%, 82%)',
  primary_300: 'hsl(255, 80%, 72%)',
  primary_400: 'hsl(255, 78%, 62%)',
  primary_500: 'hsl(255, 75%, 55%)',
  primary_600: 'hsl(255, 72%, 46%)',
  primary_700: 'hsl(255, 70%, 38%)',
  primary_800: 'hsl(255, 68%, 29%)',
  primary_900: 'hsl(255, 66%, 20%)',
  primary_950: 'hsl(255, 64%, 13%)',
  primary_975: 'hsl(255, 62%, 8%)',

  positive_25: '#ECFEF5',
  positive_50: '#D3FDE8',
  positive_100: '#A3FACF',
  positive_200: '#6AF6B0',
  positive_300: '#2CF28F',
  positive_400: '#0DD370',
  positive_500: '#09B35E',
  positive_600: '#04904A',
  positive_700: '#036D38',
  positive_800: '#04522B',
  positive_900: '#033F21',
  positive_950: '#032A17',
  positive_975: '#021D0F',

  negative_25: '#FFF5F7',
  negative_50: '#FEE7EC',
  negative_100: '#FDD3DD',
  negative_200: '#FBBBCA',
  negative_300: '#F891A9',
  negative_400: '#F65A7F',
  negative_500: '#E91646',
  negative_600: '#CA123D',
  negative_700: '#A71134',
  negative_800: '#7F0B26',
  negative_900: '#5F071C',
  negative_950: '#430413',
  negative_975: '#30030D',
}

const NYXOSKY_SUBDUED_PALETTE: Palette = {
  ...NYXOSKY_DEFAULT_PALETTE,
  black: 'hsl(245, 32%, 9%)',
  contrast_800: 'hsl(245, 20%, 22%)',
  contrast_900: 'hsl(245, 24%, 18%)',
  contrast_950: 'hsl(245, 28%, 14%)',
  contrast_975: 'hsl(245, 30%, 11%)',
  contrast_1000: 'hsl(245, 32%, 9%)',
}

// ---------------------------------------------------------------------------
// WITCHSKY
// Warm off-white contrast ramp, red-orange accent (hue 5).
// ---------------------------------------------------------------------------
const WITCHSKY_DEFAULT_PALETTE: Palette = {
  white: '#FEFBFB',
  black: '#000000',
  pink: '#dd5e8f', // formerly PINK
  yellow: YELLOW,
  like: '#dd5e8f',

  contrast_0: '#FEFBFB',
  contrast_25: '#ECE9E9',
  contrast_50: '#DBD8D8',
  contrast_100: '#C9C7C7',
  contrast_200: '#B8B6B6',
  contrast_300: '#A8A5A5',
  contrast_400: '#979595',
  contrast_500: '#878585',
  contrast_600: '#787575',
  contrast_700: '#686666',
  contrast_800: '#595757',
  contrast_900: '#4B4848',
  contrast_950: '#3D3A3A',
  contrast_975: '#1b1a1a',
  contrast_1000: '#000000',

  primary_25: 'hsl(5, 30%, 97%)',
  primary_50: 'hsl(5, 30%, 95%)',
  primary_100: 'hsl(5, 30%, 90%)',
  primary_200: 'hsl(5, 52%, 80%)',
  primary_300: 'hsl(5, 64%, 70%)',
  primary_400: 'hsl(5, 75%, 58%)',
  primary_500: 'hsl(5, 82%, 60%)',
  primary_600: 'hsl(5, 80%, 54%)',
  primary_700: 'hsl(5, 40%, 32%)',
  primary_800: 'hsl(5, 42%, 25%)',
  primary_900: 'hsl(5, 45%, 18%)',
  primary_950: 'hsl(5, 48%, 10%)',
  primary_975: 'hsl(5, 50%, 7%)',

  positive_25: '#ECFEF5',
  positive_50: '#D3FDE8',
  positive_100: '#A3FACF',
  positive_200: '#6AF6B0',
  positive_300: '#2CF28F',
  positive_400: '#0DD370',
  positive_500: '#09B35E',
  positive_600: '#04904A',
  positive_700: '#036D38',
  positive_800: '#04522B',
  positive_900: '#033F21',
  positive_950: '#032A17',
  positive_975: '#021D0F',

  negative_25: '#FFF5F7',
  negative_50: '#FEE7EC',
  negative_100: '#FDD3DD',
  negative_200: '#FBBBCA',
  negative_300: '#F891A9',
  negative_400: '#F65A7F',
  negative_500: '#E91646',
  negative_600: '#CA123D',
  negative_700: '#A71134',
  negative_800: '#7F0B26',
  negative_900: '#5F071C',
  negative_950: '#430413',
  negative_975: '#30030D',
}

const WITCHSKY_SUBDUED_PALETTE: Palette = {
  ...WITCHSKY_DEFAULT_PALETTE,
  black: '#383434',
  contrast_975: '#2F2D2D',
  contrast_1000: '#222020',
  primary_400: 'hsl(5, 84%, 66%)',
  primary_500: 'hsl(5, 82%, 60%)',
  primary_600: 'hsl(5, 80%, 54%)',
  primary_700: 'hsl(5, 58%, 55%)',
  positive_25: '#ECFEF5',
  positive_50: '#D8FDEB',
  positive_100: '#A8FAD1',
  positive_200: '#6FF6B3',
  positive_300: '#31F291',
  positive_400: '#0EDD75',
  positive_500: '#0AC266',
  positive_600: '#049F52',
  positive_700: '#038142',
  positive_800: '#056636',
  positive_900: '#04522B',
  positive_950: '#053D21',
  positive_975: '#052917',
  negative_25: '#FFF5F7',
  negative_50: '#FEEBEF',
  negative_100: '#FDD8E1',
  negative_200: '#FCC0CE',
  negative_300: '#F99AB0',
  negative_400: '#F76486',
  negative_500: '#EB2452',
  negative_600: '#D81341',
  negative_700: '#BA1239',
  negative_800: '#910D2C',
  negative_900: '#6F0B22',
  negative_950: '#500B1C',
  negative_975: '#3E0915',
}

// ---------------------------------------------------------------------------
// BLUESKY
// Neutral white/grey contrast ramp, blue accent.
// ---------------------------------------------------------------------------
const BLUESKY_DEFAULT_PALETTE: Palette = {
  white: '#FEFBFB',
  black: '#000000',
  pink: PINK,
  yellow: YELLOW,
  like: PINK,
  contrast_0: '#FFFFFF',
  contrast_25: '#F9FAFB',
  contrast_50: '#EFF2F6',
  contrast_100: '#DCE2EA',
  contrast_200: '#C0CAD8',
  contrast_300: '#A5B2C5',
  contrast_400: '#8798B0',
  contrast_500: '#667B99',
  contrast_600: '#526580',
  contrast_700: '#405168',
  contrast_800: '#313F54',
  contrast_900: '#232E3E',
  contrast_950: '#19222E',
  contrast_975: '#111822',
  contrast_1000: '#000000',
  primary_25: '#F5F9FF',
  primary_50: '#E5F0FF',
  primary_100: '#CCE1FF',
  primary_200: '#A8CCFF',
  primary_300: '#75AFFF',
  primary_400: '#4291FF',
  primary_500: '#006AFF',
  primary_600: '#0059D6',
  primary_700: '#0048AD',
  primary_800: '#00398A',
  primary_900: '#002861',
  primary_950: '#001E47',
  primary_975: '#001533',
  positive_25: '#ECFEF5',
  positive_50: '#D3FDE8',
  positive_100: '#A3FACF',
  positive_200: '#6AF6B0',
  positive_300: '#2CF28F',
  positive_400: '#0DD370',
  positive_500: '#09B35E',
  positive_600: '#04904A',
  positive_700: '#036D38',
  positive_800: '#04522B',
  positive_900: '#033F21',
  positive_950: '#032A17',
  positive_975: '#021D0F',
  negative_25: '#FFF5F7',
  negative_50: '#FEE7EC',
  negative_100: '#FDD3DD',
  negative_200: '#FBBBCA',
  negative_300: '#F891A9',
  negative_400: '#F65A7F',
  negative_500: '#E91646',
  negative_600: '#CA123D',
  negative_700: '#A71134',
  negative_800: '#7F0B26',
  negative_900: '#5F071C',
  negative_950: '#430413',
  negative_975: '#30030D',
}

const BLUESKY_SUBDUED_PALETTE: Palette = {
  ...BLUESKY_DEFAULT_PALETTE,
  contrast_0: '#FFFFFF',
  contrast_25: '#F9FAFB',
  contrast_50: '#F2F4F8',
  contrast_100: '#E2E7EE',
  contrast_200: '#C3CDDA',
  contrast_300: '#ABB8C9',
  contrast_400: '#8D9DB4',
  contrast_500: '#6F839F',
  contrast_600: '#586C89',
  contrast_700: '#485B75',
  contrast_800: '#394960',
  contrast_900: '#2C3A4E',
  contrast_950: '#222E3F',
  contrast_975: '#1C2736',
  contrast_1000: '#151D28',
  primary_25: '#F5F9FF',
  primary_50: '#EBF3FF',
  primary_100: '#D6E7FF',
  primary_200: '#ADCFFF',
  primary_300: '#80B5FF',
  primary_400: '#4D97FF',
  primary_500: '#0F73FF',
  primary_600: '#0661E0',
  primary_700: '#0A52B8',
  primary_800: '#0E4490',
  primary_900: '#123464',
  primary_950: '#122949',
  primary_975: '#122136',
  positive_25: '#ECFEF5',
  positive_50: '#D8FDEB',
  positive_100: '#A8FAD1',
  positive_200: '#6FF6B3',
  positive_300: '#31F291',
  positive_400: '#0EDD75',
  positive_500: '#0AC266',
  positive_600: '#049F52',
  positive_700: '#038142',
  positive_800: '#056636',
  positive_900: '#04522B',
  positive_950: '#053D21',
  positive_975: '#052917',
  negative_25: '#FFF5F7',
  negative_50: '#FEEBEF',
  negative_100: '#FDD8E1',
  negative_200: '#FCC0CE',
  negative_300: '#F99AB0',
  negative_400: '#F76486',
  negative_500: '#EB2452',
  negative_600: '#D81341',
  negative_700: '#BA1239',
  negative_800: '#910D2C',
  negative_900: '#6F0B22',
  negative_950: '#500B1C',
  negative_975: '#3E0915',
}

// ---------------------------------------------------------------------------
// DEER SOCIAL
// Blue-tinted (hue 211) neutral ramp, green accent (hue 145).
// ---------------------------------------------------------------------------
const DEER_DEFAULT_PALETTE: Palette = {
  white: 'hsl(211, 20%, 100%)',
  black: '#000000',
  pink: PINK,
  yellow: YELLOW,
  like: PINK,
  contrast_0: 'hsl(211, 20%, 100%)',
  contrast_25: 'hsl(211, 20%, 95.3%)',
  contrast_50: 'hsl(211, 20%, 90.6%)',
  contrast_100: 'hsl(211, 20%, 85.9%)',
  contrast_200: 'hsl(211, 20%, 81.2%)',
  contrast_300: 'hsl(211, 20%, 71.8%)',
  contrast_400: 'hsl(211, 20%, 62.4%)',
  contrast_500: 'hsl(211, 20%, 53%)',
  contrast_600: 'hsl(211, 24%, 43.6%)',
  contrast_700: 'hsl(211, 24%, 34.2%)',
  contrast_800: 'hsl(211, 28%, 24.8%)',
  contrast_900: 'hsl(211, 28%, 20.1%)',
  contrast_950: 'hsl(211, 28%, 15.4%)',
  contrast_975: 'hsl(211, 28%, 10.7%)',
  contrast_1000: '#000000',
  primary_25: 'hsl(145, 30%, 97%)',
  primary_50: 'hsl(145, 30%, 95%)',
  primary_100: 'hsl(145, 30%, 90%)',
  primary_200: 'hsl(145, 32%, 80%)',
  primary_300: 'hsl(145, 34%, 70%)',
  primary_400: 'hsl(145, 35%, 58%)',
  primary_500: 'hsl(145, 35%, 45%)',
  primary_600: 'hsl(145, 38%, 38%)',
  primary_700: 'hsl(145, 40%, 32%)',
  primary_800: 'hsl(145, 42%, 25%)',
  primary_900: 'hsl(145, 45%, 18%)',
  primary_950: 'hsl(145, 48%, 10%)',
  primary_975: 'hsl(145, 50%, 7%)',
  positive_25: 'hsl(152, 82%, 97%)',
  positive_50: 'hsl(152, 82%, 95%)',
  positive_100: 'hsl(152, 82%, 90%)',
  positive_200: 'hsl(152, 82%, 80%)',
  positive_300: 'hsl(152, 82%, 70%)',
  positive_400: 'hsl(152, 82%, 60%)',
  positive_500: 'hsl(152, 82%, 50%)',
  positive_600: 'hsl(152, 82%, 42%)',
  positive_700: 'hsl(152, 82%, 34%)',
  positive_800: 'hsl(152, 82%, 26%)',
  positive_900: 'hsl(152, 82%, 18%)',
  positive_950: 'hsl(152, 82%, 10%)',
  positive_975: 'hsl(152, 82%, 7%)',
  negative_25: 'hsl(346, 91%, 97%)',
  negative_50: 'hsl(346, 91%, 95%)',
  negative_100: 'hsl(346, 91%, 90%)',
  negative_200: 'hsl(346, 91%, 80%)',
  negative_300: 'hsl(346, 91%, 70%)',
  negative_400: 'hsl(346, 91%, 60%)',
  negative_500: 'hsl(346, 91%, 50%)',
  negative_600: 'hsl(346, 91%, 42%)',
  negative_700: 'hsl(346, 91%, 34%)',
  negative_800: 'hsl(346, 91%, 26%)',
  negative_900: 'hsl(346, 91%, 18%)',
  negative_950: 'hsl(346, 91%, 10%)',
  negative_975: 'hsl(346, 91%, 7%)',
}

const DEER_SUBDUED_PALETTE: Palette = {
  ...DEER_DEFAULT_PALETTE,
  contrast_1000: '#151D28',
  primary_25: 'hsl(140, 15%, 97%)',
  primary_50: 'hsl(140, 18%, 95%)',
  primary_100: 'hsl(140, 22%, 90%)',
  primary_200: 'hsl(140, 25%, 80%)',
  primary_300: 'hsl(140, 28%, 70%)',
  primary_400: 'hsl(140, 32%, 58%)',
  primary_500: 'hsl(140, 35%, 45%)',
  primary_600: 'hsl(140, 38%, 38%)',
  primary_700: 'hsl(140, 42%, 32%)',
  primary_800: 'hsl(140, 45%, 25%)',
  primary_900: 'hsl(140, 48%, 18%)',
  primary_950: 'hsl(140, 50%, 10%)',
  primary_975: 'hsl(140, 55%, 7%)',
}

// ---------------------------------------------------------------------------
// ZEPPELIN
// Blue-grey (hue 240) neutral ramp, golden/amber accent (hue 47).
// ---------------------------------------------------------------------------
const ZEPPELIN_DEFAULT_PALETTE: Palette = {
  white: 'hsl(240, 15%, 100%)',
  black: 'hsl(240, 23%, 6%)',
  pink: PINK,
  yellow: YELLOW,
  like: PINK,
  contrast_0: 'hsl(240, 15%, 100%)',
  contrast_25: 'hsl(240, 15%, 95.3%)',
  contrast_50: 'hsl(240, 15%, 90.6%)',
  contrast_100: 'hsl(240, 15%, 85.9%)',
  contrast_200: 'hsl(240, 15%, 81.2%)',
  contrast_300: 'hsl(240, 15%, 71.8%)',
  contrast_400: 'hsl(240, 15%, 62.4%)',
  contrast_500: 'hsl(240, 15%, 53%)',
  contrast_600: 'hsl(240, 19%, 43.6%)',
  contrast_700: 'hsl(240, 19%, 34.2%)',
  contrast_800: 'hsl(240, 23%, 24.8%)',
  contrast_900: 'hsl(240, 23%, 20.1%)',
  contrast_950: 'hsl(240, 23%, 15.4%)',
  contrast_975: 'hsl(240, 23%, 10.7%)',
  contrast_1000: 'hsl(240, 23%, 6%)',
  primary_25: 'hsl(47, 80%, 89%)',
  primary_50: 'hsl(47, 80%, 87%)',
  primary_100: 'hsl(47, 80%, 82%)',
  primary_200: 'hsl(47, 72%, 72%)',
  primary_300: 'hsl(47, 74%, 62%)',
  primary_400: 'hsl(47, 75%, 50%)',
  primary_500: 'hsl(47, 75%, 37%)',
  primary_600: 'hsl(47, 78%, 30%)',
  primary_700: 'hsl(47, 80%, 24%)',
  primary_800: 'hsl(47, 82%, 17%)',
  primary_900: 'hsl(47, 85%, 12%)',
  primary_950: 'hsl(47, 88%, 5%)',
  primary_975: 'hsl(47, 90%, 2%)',
  positive_25: 'hsl(80, 82%, 97%)',
  positive_50: 'hsl(80, 82%, 95%)',
  positive_100: 'hsl(80, 82%, 90%)',
  positive_200: 'hsl(80, 82%, 80%)',
  positive_300: 'hsl(80, 82%, 70%)',
  positive_400: 'hsl(80, 82%, 60%)',
  positive_500: 'hsl(80, 82%, 50%)',
  positive_600: 'hsl(80, 82%, 42%)',
  positive_700: 'hsl(80, 82%, 34%)',
  positive_800: 'hsl(80, 82%, 26%)',
  positive_900: 'hsl(80, 82%, 18%)',
  positive_950: 'hsl(80, 82%, 10%)',
  positive_975: 'hsl(80, 82%, 7%)',
  negative_25: 'hsl(0, 91%, 97%)',
  negative_50: 'hsl(0, 91%, 95%)',
  negative_100: 'hsl(0, 91%, 90%)',
  negative_200: 'hsl(0, 91%, 80%)',
  negative_300: 'hsl(0, 91%, 70%)',
  negative_400: 'hsl(0, 91%, 60%)',
  negative_500: 'hsl(0, 91%, 50%)',
  negative_600: 'hsl(0, 91%, 42%)',
  negative_700: 'hsl(0, 91%, 34%)',
  negative_800: 'hsl(0, 91%, 26%)',
  negative_900: 'hsl(0, 91%, 18%)',
  negative_950: 'hsl(0, 91%, 10%)',
  negative_975: 'hsl(0, 91%, 7%)',
}

const ZEPPELIN_SUBDUED_PALETTE: Palette = {
  ...ZEPPELIN_DEFAULT_PALETTE,
  black: 'hsl(240, 3%, 12%)',
  contrast_0: 'hsl(240, 20%, 100%)',
  contrast_25: 'hsl(240, 20%, 95.6%)',
  contrast_50: 'hsl(240, 20%, 91.2%)',
  contrast_100: 'hsl(240, 20%, 86.8%)',
  contrast_200: 'hsl(240, 20%, 82.4%)',
  contrast_300: 'hsl(240, 16%, 73.6%)',
  contrast_400: 'hsl(240, 16%, 64.8%)',
  contrast_500: 'hsl(240, 12%, 56%)',
  contrast_600: 'hsl(240, 12%, 47.2%)',
  contrast_700: 'hsl(240, 12%, 38.4%)',
  contrast_800: 'hsl(240, 12%, 29.6%)',
  contrast_900: 'hsl(240, 12%, 25.2%)',
  contrast_950: 'hsl(240, 12%, 20.8%)',
  contrast_975: 'hsl(240, 12%, 16.4%)',
  contrast_1000: 'hsl(240, 12%, 12%)',
  primary_25: 'hsl(47, 60%, 97%)',
  primary_50: 'hsl(47, 63%, 94%)',
  primary_100: 'hsl(47, 65%, 88%)',
  primary_200: 'hsl(47, 70%, 78%)',
  primary_300: 'hsl(47, 73%, 68%)',
  primary_400: 'hsl(47, 77%, 58%)',
  primary_500: 'hsl(47, 80%, 45%)',
  primary_600: 'hsl(47, 83%, 38%)',
  primary_700: 'hsl(47, 87%, 30%)',
  primary_800: 'hsl(47, 90%, 25%)',
  primary_900: 'hsl(47, 93%, 18%)',
  primary_950: 'hsl(47, 95%, 10%)',
  primary_975: 'hsl(47, 98%, 7%)',
}

// ---------------------------------------------------------------------------
// KITTY
// Bluesky-style contrast ramp, purple accent (hue 290).
// ---------------------------------------------------------------------------
const KITTY_DEFAULT_PALETTE: Palette = {
  white: '#FEFBFB',
  black: '#000000',
  pink: PINK,
  yellow: YELLOW,
  like: PINK,
  contrast_0: '#FFFFFF',
  contrast_25: '#F9FAFB',
  contrast_50: '#EFF2F6',
  contrast_100: '#DCE2EA',
  contrast_200: '#C0CAD8',
  contrast_300: '#A5B2C5',
  contrast_400: '#8798B0',
  contrast_500: '#667B99',
  contrast_600: '#526580',
  contrast_700: '#405168',
  contrast_800: '#313F54',
  contrast_900: '#232E3E',
  contrast_950: '#19222E',
  contrast_975: '#111822',
  contrast_1000: '#000000',
  primary_25: 'hsl(290, 30%, 97%)',
  primary_50: 'hsl(290, 30%, 95%)',
  primary_100: 'hsl(290, 30%, 90%)',
  primary_200: 'hsl(290, 32%, 80%)',
  primary_300: 'hsl(290, 34%, 70%)',
  primary_400: 'hsl(290, 35%, 58%)',
  primary_500: 'hsl(290, 35%, 45%)',
  primary_600: 'hsl(290, 38%, 38%)',
  primary_700: 'hsl(290, 40%, 32%)',
  primary_800: 'hsl(290, 42%, 25%)',
  primary_900: 'hsl(290, 45%, 18%)',
  primary_950: 'hsl(290, 48%, 10%)',
  primary_975: 'hsl(290, 50%, 7%)',
  positive_25: '#ECFEF5',
  positive_50: '#D3FDE8',
  positive_100: '#A3FACF',
  positive_200: '#6AF6B0',
  positive_300: '#2CF28F',
  positive_400: '#0DD370',
  positive_500: '#09B35E',
  positive_600: '#04904A',
  positive_700: '#036D38',
  positive_800: '#04522B',
  positive_900: '#033F21',
  positive_950: '#032A17',
  positive_975: '#021D0F',
  negative_25: '#FFF5F7',
  negative_50: '#FEE7EC',
  negative_100: '#FDD3DD',
  negative_200: '#FBBBCA',
  negative_300: '#F891A9',
  negative_400: '#F65A7F',
  negative_500: '#E91646',
  negative_600: '#CA123D',
  negative_700: '#A71134',
  negative_800: '#7F0B26',
  negative_900: '#5F071C',
  negative_950: '#430413',
  negative_975: '#30030D',
}

const KITTY_SUBDUED_PALETTE: Palette = {
  ...KITTY_DEFAULT_PALETTE,
  contrast_0: '#FFFFFF',
  contrast_25: '#F9FAFB',
  contrast_50: '#F2F4F8',
  contrast_100: '#E2E7EE',
  contrast_200: '#C3CDDA',
  contrast_300: '#ABB8C9',
  contrast_400: '#8D9DB4',
  contrast_500: '#6F839F',
  contrast_600: '#586C89',
  contrast_700: '#485B75',
  contrast_800: '#394960',
  contrast_900: '#2C3A4E',
  contrast_950: '#222E3F',
  contrast_975: '#1C2736',
  contrast_1000: '#151D28',
  primary_25: 'hsl(290, 30%, 97%)',
  primary_50: 'hsl(290, 30%, 96%)',
  primary_100: 'hsl(290, 30%, 92%)',
  primary_200: 'hsl(290, 32%, 81%)',
  primary_300: 'hsl(290, 34%, 72%)',
  primary_400: 'hsl(290, 35%, 60%)',
  primary_500: 'hsl(290, 35%, 48%)',
  primary_600: 'hsl(290, 38%, 41%)',
  primary_700: 'hsl(290, 40%, 36%)',
  primary_800: 'hsl(290, 42%, 29%)',
  primary_900: 'hsl(290, 45%, 22%)',
  primary_950: 'hsl(290, 48%, 10%)',
  primary_975: 'hsl(290, 50%, 8%)',
  positive_25: '#ECFEF5',
  positive_50: '#D8FDEB',
  positive_100: '#A8FAD1',
  positive_200: '#6FF6B3',
  positive_300: '#31F291',
  positive_400: '#0EDD75',
  positive_500: '#0AC266',
  positive_600: '#049F52',
  positive_700: '#038142',
  positive_800: '#056636',
  positive_900: '#04522B',
  positive_950: '#053D21',
  positive_975: '#052917',
  negative_25: '#FFF5F7',
  negative_50: '#FEEBEF',
  negative_100: '#FDD8E1',
  negative_200: '#FCC0CE',
  negative_300: '#F99AB0',
  negative_400: '#F76486',
  negative_500: '#EB2452',
  negative_600: '#D81341',
  negative_700: '#BA1239',
  negative_800: '#910D2C',
  negative_900: '#6F0B22',
  negative_950: '#500B1C',
  negative_975: '#3E0915',
}

// ---------------------------------------------------------------------------
// RED DWARF
// Warm reddish-brown custom grey scale, dark red-brown accent.
// ---------------------------------------------------------------------------
const REDDWARF_DEFAULT_PALETTE: Palette = {
  white: '#FEFBFB',
  black: '#000000',
  pink: '#FF6B6B', // formerly PINK
  yellow: YELLOW,
  like: '#FF6B6B',
  contrast_0: '#fff7f5',
  contrast_25: '#fff7f5',
  contrast_50: '#fff0ee',
  contrast_100: '#fff0ee',
  contrast_200: '#f8e2de',
  contrast_300: '#eaccc7',
  contrast_400: '#bd948e',
  contrast_500: '#93655f',
  contrast_600: '#93655f',
  contrast_700: '#714842',
  contrast_800: '#714842',
  contrast_900: '#5b342f',
  contrast_950: '#401d19',
  contrast_975: '#2c0c09',
  contrast_1000: '#180000',
  primary_25: '#fff7f5',
  primary_50: '#fff0ee',
  primary_100: '#f8e2de',
  primary_200: '#f8e2de',
  primary_300: '#eaccc7',
  primary_400: '#bd948e',
  primary_500: '#c14c42',
  primary_600: '#714842',
  primary_700: '#714842',
  primary_800: '#5b342f',
  primary_900: '#5b342f',
  primary_950: '#401d19',
  primary_975: '#2c0c09',
  positive_25: '#ECFEF5',
  positive_50: '#D3FDE8',
  positive_100: '#A3FACF',
  positive_200: '#6AF6B0',
  positive_300: '#2CF28F',
  positive_400: '#0DD370',
  positive_500: '#09B35E',
  positive_600: '#04904A',
  positive_700: '#036D38',
  positive_800: '#04522B',
  positive_900: '#033F21',
  positive_950: '#032A17',
  positive_975: '#021D0F',
  negative_25: '#FFF5F7',
  negative_50: '#FEE7EC',
  negative_100: '#FDD3DD',
  negative_200: '#FBBBCA',
  negative_300: '#F891A9',
  negative_400: '#F65A7F',
  negative_500: '#E91646',
  negative_600: '#CA123D',
  negative_700: '#A71134',
  negative_800: '#7F0B26',
  negative_900: '#5F071C',
  negative_950: '#430413',
  negative_975: '#30030D',
}

const REDDWARF_SUBDUED_PALETTE: Palette = {
  ...REDDWARF_DEFAULT_PALETTE,
  contrast_1000: '#2c0c09',
  positive_25: '#ECFEF5',
  positive_50: '#D8FDEB',
  positive_100: '#A8FAD1',
  positive_200: '#6FF6B3',
  positive_300: '#31F291',
  positive_400: '#0EDD75',
  positive_500: '#0AC266',
  positive_600: '#049F52',
  positive_700: '#038142',
  positive_800: '#056636',
  positive_900: '#04522B',
  positive_950: '#053D21',
  positive_975: '#052917',
  negative_25: '#FFF5F7',
  negative_50: '#FEEBEF',
  negative_100: '#FDD8E1',
  negative_200: '#FCC0CE',
  negative_300: '#F99AB0',
  negative_400: '#F76486',
  negative_500: '#EB2452',
  negative_600: '#D81341',
  negative_700: '#BA1239',
  negative_800: '#910D2C',
  negative_900: '#6F0B22',
  negative_950: '#500B1C',
  negative_975: '#3E0915',
}

// ---------------------------------------------------------------------------
// CATPPUCCIN
// Warm beige-grey (Latte) in light, deep purple-grey (Macchiato) in dim.
// ---------------------------------------------------------------------------
const CATPPUCCIN_DEFAULT_PALETTE: Palette = {
  white: '#FEFBFB',
  black: '#000000',
  pink: PINK,
  yellow: YELLOW,
  like: PINK,
  contrast_0: '#EFF1F5',
  contrast_25: '#D3D7DE',
  contrast_50: '#E6E9EF',
  contrast_100: '#ccd0da',
  contrast_200: '#bcc0cc',
  contrast_300: '#acb0be',
  contrast_400: '#9ca0b0',
  contrast_500: '#8c8fa1',
  contrast_600: '#7c7f93',
  contrast_700: '#585b70',
  contrast_800: '#45475a',
  contrast_900: '#313244',
  contrast_950: '#181825',
  contrast_975: '#11111b',
  contrast_1000: '#1e1e2e',
  primary_25: 'hsl(266, 30%, 97%)',
  primary_50: 'hsl(266, 30%, 95%)',
  primary_100: 'hsl(266, 30%, 90%)',
  primary_200: 'hsl(266, 42%, 80%)',
  primary_300: 'hsl(266, 54%, 70%)',
  primary_400: 'hsl(266, 65%, 58%)',
  primary_500: 'hsl(266, 75%, 58%)',
  primary_600: 'hsl(266, 68%, 38%)',
  primary_700: 'hsl(266, 50%, 32%)',
  primary_800: 'hsl(266, 42%, 25%)',
  primary_900: 'hsl(266, 45%, 18%)',
  primary_950: 'hsl(266, 48%, 10%)',
  primary_975: 'hsl(266, 50%, 7%)',
  positive_25: '#ECFEF5',
  positive_50: '#D3FDE8',
  positive_100: '#A3FACF',
  positive_200: '#6AF6B0',
  positive_300: '#2CF28F',
  positive_400: '#0DD370',
  positive_500: '#09B35E',
  positive_600: '#04904A',
  positive_700: '#036D38',
  positive_800: '#04522B',
  positive_900: '#033F21',
  positive_950: '#032A17',
  positive_975: '#021D0F',
  negative_25: '#FFF5F7',
  negative_50: '#FEE7EC',
  negative_100: '#FDD3DD',
  negative_200: '#FBBBCA',
  negative_300: '#F891A9',
  negative_400: '#F65A7F',
  negative_500: '#E91646',
  negative_600: '#CA123D',
  negative_700: '#A71134',
  negative_800: '#7F0B26',
  negative_900: '#5F071C',
  negative_950: '#430413',
  negative_975: '#30030D',
}

const CATPPUCCIN_SUBDUED_PALETTE: Palette = {
  white: '#FEFBFB',
  black: '#000000',
  pink: PINK,
  yellow: YELLOW,
  like: PINK,
  contrast_0: '#EFF1F5',
  contrast_25: '#D3D7DE',
  contrast_50: '#E6E9EF',
  contrast_100: '#ccd0da',
  contrast_200: '#bcc0cc',
  contrast_300: '#acb0be',
  contrast_400: '#939ab7',
  contrast_500: '#8087a2',
  contrast_600: '#6e738d',
  contrast_700: '#5b6078',
  contrast_800: '#494d64',
  contrast_900: '#363a4f',
  contrast_950: '#1e2030',
  contrast_975: '#181926',
  contrast_1000: '#24273a',
  primary_25: 'hsl(267, 30%, 97%)',
  primary_50: 'hsl(267, 40%, 96%)',
  primary_100: 'hsl(267, 50%, 92%)',
  primary_200: 'hsl(267, 62%, 81%)',
  primary_300: 'hsl(267, 83%, 80%)',
  primary_400: 'hsl(267, 75%, 78%)',
  primary_500: 'hsl(267, 74%, 72%)',
  primary_600: 'hsl(267, 38%, 41%)',
  primary_700: 'hsl(267, 40%, 36%)',
  primary_800: 'hsl(267, 42%, 29%)',
  primary_900: 'hsl(267, 45%, 22%)',
  primary_950: 'hsl(267, 48%, 10%)',
  primary_975: 'hsl(267, 50%, 8%)',
  positive_25: '#ECFEF5',
  positive_50: '#D8FDEB',
  positive_100: '#A8FAD1',
  positive_200: '#6FF6B3',
  positive_300: '#31F291',
  positive_400: '#0EDD75',
  positive_500: '#0AC266',
  positive_600: '#049F52',
  positive_700: '#038142',
  positive_800: '#056636',
  positive_900: '#04522B',
  positive_950: '#053D21',
  positive_975: '#052917',
  negative_25: '#FFF5F7',
  negative_50: '#FEEBEF',
  negative_100: '#FDD8E1',
  negative_200: '#FCC0CE',
  negative_300: '#F99AB0',
  negative_400: '#F76486',
  negative_500: '#EB2452',
  negative_600: '#D81341',
  negative_700: '#BA1239',
  negative_800: '#910D2C',
  negative_900: '#6F0B22',
  negative_950: '#500B1C',
  negative_975: '#3E0915',
}

// ---------------------------------------------------------------------------
// EVERGARDEN
// Soft mossy green backgrounds, multi-hue teal/sage accent.
// ---------------------------------------------------------------------------
const EVERGARDEN_DEFAULT_PALETTE: Palette = {
  white: '#F8F9E8',
  black: '#171C1F',
  pink: '#F3C0E5',
  yellow: '#F5D098',
  like: '#F3C0E5',
  contrast_0: '#F8F9E8',
  contrast_25: '#E1EDD6',
  contrast_50: '#D3E7E0',
  contrast_100: '#C3D7D0',
  contrast_200: '#ADC9BC',
  contrast_300: '#96B4AA',
  contrast_400: '#839E9A',
  contrast_500: '#7A9493',
  contrast_600: '#6F8788',
  contrast_700: '#4A585C',
  contrast_800: '#374145',
  contrast_900: '#262F33',
  contrast_950: '#1E2528',
  contrast_975: '#191E21',
  contrast_1000: '#171C1F',
  primary_25: 'hsl(64, 30%, 97%)',
  primary_50: 'hsl(64, 30%, 95%)',
  primary_100: 'hsl(64, 30%, 90%)',
  primary_200: 'hsl(72, 42%, 86%)',
  primary_300: 'hsl(74, 54%, 84%)',
  primary_400: 'hsl(83, 46%, 83%)',
  primary_500: 'hsl(150, 24%, 64%)',
  primary_600: 'hsl(149, 44%, 72%)',
  primary_700: 'hsl(167, 35%, 60%)',
  primary_800: 'hsl(187, 42%, 36%)',
  primary_900: 'hsl(194, 32%, 25%)',
  primary_950: 'hsl(198, 18.4%, 14.8%)',
  primary_975: 'hsl(200, 50%, 7%)',
  positive_25: '#ECFEFA',
  positive_50: '#E2FAF5',
  positive_100: '#D8F6F0',
  positive_200: '#CFF2EA',
  positive_300: '#C5EEE5',
  positive_400: '#BCEAE0',
  positive_500: '#B3E6DB',
  positive_600: '#82C4B6',
  positive_700: '#58A393',
  positive_800: '#378271',
  positive_900: '#1D6052',
  positive_950: '#0C3E34',
  positive_975: '#021D17',
  negative_25: '#FFF5F5',
  negative_50: '#FDE1E1',
  negative_100: '#FCCDCE',
  negative_200: '#FAB9BB',
  negative_300: '#F8A5A7',
  negative_400: '#F79295',
  negative_500: '#F57F82',
  negative_600: '#D45E61',
  negative_700: '#B34245',
  negative_800: '#932B2D',
  negative_900: '#72181B',
  negative_950: '#510B0D',
  negative_975: '#300304',
}

const EVERGARDEN_SUBDUED_PALETTE: Palette = {
  ...EVERGARDEN_DEFAULT_PALETTE,
  contrast_900: '#2B3337',
  contrast_950: '#232A2E',
  contrast_975: '#1C2225',
  contrast_1000: '#171C1F',
  primary_950: 'hsl(216, 48%, 10%)',
  primary_975: 'hsl(220, 50%, 7%)',
}

// ---------------------------------------------------------------------------
// FOREST
// Rich green-tinted background, vivid green accent.
// ---------------------------------------------------------------------------
const FOREST_DEFAULT_PALETTE: Palette = {
  white: 'hsl(145, 20%, 100%)',
  black: 'hsl(145, 28%, 6%)',
  pink: PINK,
  yellow: YELLOW,
  like: PINK,

  contrast_0: 'hsl(145, 20%, 100%)',
  contrast_25: 'hsl(145, 20%, 95.3%)',
  contrast_50: 'hsl(145, 20%, 90.6%)',
  contrast_100: 'hsl(145, 20%, 85.9%)',
  contrast_200: 'hsl(145, 20%, 81.2%)',
  contrast_300: 'hsl(145, 20%, 71.8%)',
  contrast_400: 'hsl(145, 20%, 62.4%)',
  contrast_500: 'hsl(145, 20%, 53%)',
  contrast_600: 'hsl(145, 24%, 43.6%)',
  contrast_700: 'hsl(145, 24%, 34.2%)',
  contrast_800: 'hsl(145, 28%, 24.8%)',
  contrast_900: 'hsl(145, 28%, 20.1%)',
  contrast_950: 'hsl(145, 28%, 15.4%)',
  contrast_975: 'hsl(145, 28%, 10.7%)',
  contrast_1000: 'hsl(145, 28%, 6%)',

  primary_25: 'hsl(145, 30%, 97%)',
  primary_50: 'hsl(145, 30%, 95%)',
  primary_100: 'hsl(145, 30%, 90%)',
  primary_200: 'hsl(145, 32%, 80%)',
  primary_300: 'hsl(145, 34%, 70%)',
  primary_400: 'hsl(145, 35%, 58%)',
  primary_500: 'hsl(145, 35%, 45%)',
  primary_600: 'hsl(145, 38%, 38%)',
  primary_700: 'hsl(145, 40%, 32%)',
  primary_800: 'hsl(145, 42%, 25%)',
  primary_900: 'hsl(145, 45%, 18%)',
  primary_950: 'hsl(145, 48%, 10%)',
  primary_975: 'hsl(145, 50%, 7%)',

  positive_25: 'hsl(120, 82%, 97%)',
  positive_50: 'hsl(120, 82%, 95%)',
  positive_100: 'hsl(120, 82%, 90%)',
  positive_200: 'hsl(120, 82%, 80%)',
  positive_300: 'hsl(120, 82%, 70%)',
  positive_400: 'hsl(120, 82%, 60%)',
  positive_500: 'hsl(120, 82%, 50%)',
  positive_600: 'hsl(120, 82%, 42%)',
  positive_700: 'hsl(120, 82%, 34%)',
  positive_800: 'hsl(120, 82%, 26%)',
  positive_900: 'hsl(120, 82%, 18%)',
  positive_950: 'hsl(120, 82%, 10%)',
  positive_975: 'hsl(120, 82%, 7%)',

  negative_25: '#FFF5F7',
  negative_50: '#FEE7EC',
  negative_100: '#FDD3DD',
  negative_200: '#FBBBCA',
  negative_300: '#F891A9',
  negative_400: '#F65A7F',
  negative_500: '#E91646',
  negative_600: '#CA123D',
  negative_700: '#A71134',
  negative_800: '#7F0B26',
  negative_900: '#5F071C',
  negative_950: '#430413',
  negative_975: '#30030D',
}

const FOREST_SUBDUED_PALETTE: Palette = {
  ...FOREST_DEFAULT_PALETTE,
  black: 'hsl(145, 28%, 9%)',
  contrast_800: 'hsl(145, 28%, 22%)',
  contrast_900: 'hsl(145, 28%, 18%)',
  contrast_950: 'hsl(145, 28%, 14%)',
  contrast_975: 'hsl(145, 28%, 11%)',
  contrast_1000: 'hsl(145, 28%, 9%)',
  primary_25: 'hsl(140, 15%, 97%)',
  primary_50: 'hsl(140, 18%, 95%)',
  primary_100: 'hsl(140, 22%, 90%)',
  primary_200: 'hsl(140, 25%, 80%)',
  primary_300: 'hsl(140, 28%, 70%)',
  primary_400: 'hsl(140, 32%, 58%)',
  primary_500: 'hsl(140, 35%, 45%)',
  primary_600: 'hsl(140, 38%, 38%)',
  primary_700: 'hsl(140, 42%, 32%)',
  primary_800: 'hsl(140, 45%, 25%)',
  primary_900: 'hsl(140, 48%, 18%)',
  primary_950: 'hsl(140, 50%, 10%)',
  primary_975: 'hsl(140, 55%, 7%)',
}

// ---------------------------------------------------------------------------
// DARK CARNIVAL
// True black base with red-warm surfaces, crimson accent, gold like button.
// ---------------------------------------------------------------------------
const DARKCARNIVAL_DEFAULT_PALETTE: Palette = {
  white: 'hsl(0, 0%, 100%)',
  black: 'hsl(0, 20%, 4%)',
  pink: YELLOW, // formerly PINK, '#CC8800'
  yellow: YELLOW,
  like: YELLOW, // ✨ gold

  contrast_0: 'hsl(0, 0%, 100%)',
  contrast_25: 'hsl(0, 10%, 95%)',
  contrast_50: 'hsl(0, 10%, 88%)',
  contrast_100: 'hsl(0, 10%, 78%)',
  contrast_200: 'hsl(0, 10%, 66%)',
  contrast_300: 'hsl(0, 10%, 54%)',
  contrast_400: 'hsl(0, 10%, 43%)',
  contrast_500: 'hsl(0, 12%, 33%)',
  contrast_600: 'hsl(0, 14%, 25%)',
  contrast_700: 'hsl(0, 16%, 18%)',
  contrast_800: 'hsl(0, 18%, 13%)',
  contrast_900: 'hsl(0, 20%, 9%)',
  contrast_950: 'hsl(0, 20%, 7%)',
  contrast_975: 'hsl(0, 20%, 5%)',
  contrast_1000: 'hsl(0, 20%, 4%)',

  primary_25: 'hsl(0, 80%, 98%)',
  primary_50: 'hsl(0, 80%, 95%)',
  primary_100: 'hsl(0, 80%, 88%)',
  primary_200: 'hsl(0, 78%, 78%)',
  primary_300: 'hsl(0, 76%, 66%)',
  primary_400: 'hsl(0, 74%, 55%)',
  primary_500: 'hsl(0, 72%, 44%)',
  primary_600: 'hsl(0, 72%, 36%)',
  primary_700: 'hsl(0, 70%, 28%)',
  primary_800: 'hsl(0, 68%, 20%)',
  primary_900: 'hsl(0, 66%, 14%)',
  primary_950: 'hsl(0, 64%, 9%)',
  primary_975: 'hsl(0, 62%, 6%)',

  positive_25: '#ECFEF5',
  positive_50: '#D3FDE8',
  positive_100: '#A3FACF',
  positive_200: '#6AF6B0',
  positive_300: '#2CF28F',
  positive_400: '#0DD370',
  positive_500: '#09B35E',
  positive_600: '#04904A',
  positive_700: '#036D38',
  positive_800: '#04522B',
  positive_900: '#033F21',
  positive_950: '#032A17',
  positive_975: '#021D0F',

  negative_25: '#FFF5F7',
  negative_50: '#FEE7EC',
  negative_100: '#FDD3DD',
  negative_200: '#FBBBCA',
  negative_300: '#F891A9',
  negative_400: '#F65A7F',
  negative_500: '#E91646',
  negative_600: '#CA123D',
  negative_700: '#A71134',
  negative_800: '#7F0B26',
  negative_900: '#5F071C',
  negative_950: '#430413',
  negative_975: '#30030D',
}

const DARKCARNIVAL_SUBDUED_PALETTE: Palette = {
  ...DARKCARNIVAL_DEFAULT_PALETTE,
  black: 'hsl(0, 18%, 8%)',
  contrast_950: 'hsl(0, 18%, 11%)',
  contrast_975: 'hsl(0, 18%, 9%)',
  contrast_1000: 'hsl(0, 18%, 8%)',
}

// ---------------------------------------------------------------------------
// SAKURA
// Warm rose-pink (hue 330) contrast ramp, hot-pink primary accent.
// ---------------------------------------------------------------------------
const SAKURA_DEFAULT_PALETTE: Palette = {
  white: 'hsl(330, 60%, 94%)',
  black: 'hsl(330, 30%, 8%)',
  pink: SAKURA_LIKE,
  yellow: YELLOW,
  like: SAKURA_LIKE,

  contrast_0: 'hsl(330, 60%, 94%)',
  contrast_25: 'hsl(330, 55%, 90%)',
  contrast_50: 'hsl(330, 50%, 86%)',
  contrast_100: 'hsl(330, 40%, 80%)',
  contrast_200: 'hsl(330, 24%, 78%)',
  contrast_300: 'hsl(330, 22%, 67%)',
  contrast_400: 'hsl(330, 20%, 56%)',
  contrast_500: 'hsl(330, 20%, 46%)',
  contrast_600: 'hsl(330, 22%, 37%)',
  contrast_700: 'hsl(330, 24%, 28%)',
  contrast_800: 'hsl(330, 28%, 20%)',
  contrast_900: 'hsl(330, 30%, 15%)',
  contrast_950: 'hsl(330, 30%, 11%)',
  contrast_975: 'hsl(330, 30%, 9%)',
  contrast_1000: 'hsl(330, 30%, 8%)',

  primary_25: 'hsl(330, 90%, 98%)',
  primary_50: 'hsl(330, 88%, 95%)',
  primary_100: 'hsl(330, 85%, 90%)',
  primary_200: 'hsl(330, 82%, 82%)',
  primary_300: 'hsl(330, 80%, 72%)',
  primary_400: 'hsl(330, 78%, 62%)',
  primary_500: 'hsl(330, 75%, 54%)',
  primary_600: 'hsl(330, 72%, 44%)',
  primary_700: 'hsl(330, 70%, 35%)',
  primary_800: 'hsl(330, 68%, 26%)',
  primary_900: 'hsl(330, 66%, 18%)',
  primary_950: 'hsl(330, 64%, 11%)',
  primary_975: 'hsl(330, 62%, 7%)',

  positive_25: '#ECFEF5',
  positive_50: '#D3FDE8',
  positive_100: '#A3FACF',
  positive_200: '#6AF6B0',
  positive_300: '#2CF28F',
  positive_400: '#0DD370',
  positive_500: '#09B35E',
  positive_600: '#04904A',
  positive_700: '#036D38',
  positive_800: '#04522B',
  positive_900: '#033F21',
  positive_950: '#032A17',
  positive_975: '#021D0F',

  negative_25: '#FFF5F7',
  negative_50: '#FEE7EC',
  negative_100: '#FDD3DD',
  negative_200: '#FBBBCA',
  negative_300: '#F891A9',
  negative_400: '#F65A7F',
  negative_500: '#E91646',
  negative_600: '#CA123D',
  negative_700: '#A71134',
  negative_800: '#7F0B26',
  negative_900: '#5F071C',
  negative_950: '#430413',
  negative_975: '#30030D',
}

const SAKURA_SUBDUED_PALETTE: Palette = {
  ...SAKURA_DEFAULT_PALETTE,
  black: 'hsl(330, 26%, 11%)',
  contrast_800: 'hsl(330, 26%, 22%)',
  contrast_900: 'hsl(330, 28%, 18%)',
  contrast_950: 'hsl(330, 28%, 14%)',
  contrast_975: 'hsl(330, 28%, 12%)',
  contrast_1000: 'hsl(330, 26%, 11%)',
}

// ---------------------------------------------------------------------------
// Preset registry
// ---------------------------------------------------------------------------
export const THEME_PRESETS = {
  nyxoSky: {
    label: 'Nyxo Sky',
    themes: createThemes({
      defaultPalette: NYXOSKY_DEFAULT_PALETTE,
      subduedPalette: NYXOSKY_SUBDUED_PALETTE,
    }),
  },
  witchsky: {
    label: 'Witchsky',
    themes: createThemes({
      defaultPalette: WITCHSKY_DEFAULT_PALETTE,
      subduedPalette: WITCHSKY_SUBDUED_PALETTE,
    }),
  },
  bluesky: {
    label: 'Bluesky',
    themes: createThemes({
      defaultPalette: BLUESKY_DEFAULT_PALETTE,
      subduedPalette: BLUESKY_SUBDUED_PALETTE,
    }),
  },
  forest: {
    label: 'Forest',
    themes: createThemes({
      defaultPalette: FOREST_DEFAULT_PALETTE,
      subduedPalette: FOREST_SUBDUED_PALETTE,
    }),
  },
  deerSocial: {
    label: 'Deer Social',
    themes: createThemes({
      defaultPalette: DEER_DEFAULT_PALETTE,
      subduedPalette: DEER_SUBDUED_PALETTE,
    }),
  },
  zeppelin: {
    label: 'Zeppelin',
    themes: createThemes({
      defaultPalette: ZEPPELIN_DEFAULT_PALETTE,
      subduedPalette: ZEPPELIN_SUBDUED_PALETTE,
    }),
  },
  kitty: {
    label: 'Kitty',
    themes: createThemes({
      defaultPalette: KITTY_DEFAULT_PALETTE,
      subduedPalette: KITTY_SUBDUED_PALETTE,
    }),
  },
  redDwarf: {
    label: 'Red Dwarf',
    themes: createThemes({
      defaultPalette: REDDWARF_DEFAULT_PALETTE,
      subduedPalette: REDDWARF_SUBDUED_PALETTE,
    }),
  },
  catppuccin: {
    label: 'Catppuccin',
    themes: createThemes({
      defaultPalette: CATPPUCCIN_DEFAULT_PALETTE,
      subduedPalette: CATPPUCCIN_SUBDUED_PALETTE,
    }),
  },
  evergarden: {
    label: 'Evergarden',
    themes: createThemes({
      defaultPalette: EVERGARDEN_DEFAULT_PALETTE,
      subduedPalette: EVERGARDEN_SUBDUED_PALETTE,
    }),
  },
  darkCarnival: {
    label: 'Dark Carnival',
    themes: createThemes({
      defaultPalette: DARKCARNIVAL_DEFAULT_PALETTE,
      subduedPalette: DARKCARNIVAL_SUBDUED_PALETTE,
    }),
  },
  sakura: {
    label: 'Sakura',
    themes: createThemes({
      defaultPalette: SAKURA_DEFAULT_PALETTE,
      subduedPalette: SAKURA_SUBDUED_PALETTE,
    }),
  },
} as const satisfies Record<
  string,
  {label: string; themes: ReturnType<typeof createThemes>}
>

export type ThemePresetName = keyof typeof THEME_PRESETS
export const THEME_PRESET_NAMES = Object.keys(
  THEME_PRESETS,
) as ThemePresetName[]
